/**
 * ⛔ Mercadoria nao entra sem endereco ([DA-618]).
 *
 * Decisao do Ruan, 26/08/2026: *"quando nao tem prateleira a importacao trava, nao deixa importar"*.
 * E a raiz do defeito que a [DA-617] tinha consertado pela ponta de baixo — la a exclusao aprendeu
 * a tirar do mesmo lugar em que a entrada pos; aqui o lugar passa a SEMPRE existir, e desfazer
 * deixa de depender de adivinhacao.
 *
 * Mora no `shared` porque a regra vale nos DOIS lados: o front trava o botao e diz o que falta, o
 * backend recusa a gravacao. Escrever a mesma condicao duas vezes ja falhou por definicao — a casa
 * cobra isso desde a [DA-463] (*"quando dois caminhos tem que devolver a mesma coisa, o certo e nao
 * existirem dois"*).
 *
 * Vale **daqui pra frente**: as 36 notas ja gravadas sem lugar (82 itens, medidos em 26/08) seguem
 * cobertas pelo `retirarDoFundo` da [DA-617], que continua existindo por causa delas.
 */

/** So o que esta regra precisa saber de um item — proposital, pra servir aos dois lados. */
export interface ItemComLugar {
  produto_id?: number | null;
  localizacao_id?: number | null;
  numero_item?: number | null;
}

/**
 * Quais itens da nota estao sem prateleira escolhida.
 *
 * Duas isencoes, e as duas existem porque a pergunta nao tem objeto — nao por indulgencia:
 *
 * 1. **`atualizarEstoque` falso**: nenhuma mercadoria entra em prateleira nenhuma (o backend so
 *    move estoque dentro de `if (atualizar_estoque && item.produto_id)`). Recusar sem objeto ensina
 *    o operador a ignorar a trava, que e o custo que a [DA-410] cobra de todo alarme falso.
 * 2. **Item sem `produto_id`**: nao move estoque, e ja e barrado antes por "sem produto vinculado".
 *    Cobrar prateleira de quem nao tem produto seria pedir o impossivel — e recusa cuja saida nao
 *    existe e beco, nao portao ([DA-560]).
 *
 * Devolve os indices na ordem da lista, pra quem chama poder marcar a linha culpada.
 */
export function itensSemPrateleira(itens: ItemComLugar[], atualizarEstoque: boolean): number[] {
  if (!atualizarEstoque) return [];
  const fora: number[] = [];
  itens.forEach((item, idx) => {
    if (item?.produto_id && !item?.localizacao_id) fora.push(idx);
  });
  return fora;
}

/**
 * Os rotulos das linhas culpadas, na numeracao que a coluna `#` da tela mostra.
 *
 * ⚠️ Contador sem o numero do item obriga o operador a varrer a nota inteira procurando quem esta
 * cobrando — o mesmo motivo pelo qual o portao do catalogo ([DA-598]) ja lista os seus.
 * Cai no indice + 1 quando o item nao trouxe `numero_item`, que e o que a coluna exibe nesse caso.
 */
export function rotulosSemPrateleira(itens: ItemComLugar[], indices: number[]): string {
  return indices.map(idx => `#${itens[idx]?.numero_item ?? idx + 1}`).join(', ');
}
